import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/config";

export const Logo = ({ size = 36 }) => (
  <img
    src="/logo.png"
    alt="Mp Digital"
    width={size}
    height={size}
    className="rounded-full"
    data-testid="logo-mark"
  />
);

const links = [
  { label: "Servizi", href: "#servizi" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Metodo", href: "#metodo" },
  { label: "Contatti", href: "#contatti" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-5xl rounded-full border backdrop-blur-xl transition-colors duration-500 ${
          scrolled ? "bg-zinc-950/80 border-white/10 shadow-2xl" : "bg-zinc-950/40 border-white/5"
        }`}
        data-testid="main-nav"
      >
        <div className="flex items-center justify-between px-5 py-3">
          <button onClick={() => go("#top")} className="flex items-center gap-3 group" data-testid="nav-logo">
            <Logo />
            <span className="font-syne font-bold text-lg tracking-tight">
              MPX<span className="text-cyan-400"> Digital</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="text-sm text-zinc-400 hover:text-zinc-50 transition-colors duration-300"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("#contatti")}
              className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-zinc-950 transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_24px_rgba(56,189,248,0.4)]"
              data-testid="nav-cta-button"
            >
              Richiedi Preventivo
              <ArrowUpRight size={16} />
            </button>
            <button
              onClick={() => setOpen(!open)}
              className="md:hidden text-zinc-200 p-2"
              data-testid="mobile-menu-button"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-zinc-950/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8 md:hidden"
            data-testid="mobile-menu"
          >
            {links.map((l, i) => (
              <motion.button
                key={l.href}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => go(l.href)}
                className="font-syne text-3xl font-bold text-zinc-200 hover:text-cyan-400 transition-colors"
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              onClick={() => go("#contatti")}
              className="mt-4 rounded-full bg-cyan-400 px-8 py-3 font-semibold text-zinc-950"
              data-testid="mobile-nav-cta-button"
            >
              Richiedi Preventivo
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
